/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type { Action, Dispatch, ThunkAction } from '../types';
import azURL, { postATGData } from '../config/serviceAPI';
import actionConstants from '../types/action';
import { fetchOrderDetails } from './details';
import { cartConstants } from '../constants/cart';
import { type QueryClient } from '@tanstack/react-query';
import { type GetShipmentOptionsMutation } from '@/features/orders';
import type { PageName } from '@/types/analytics';
import { type PromoDataFromState } from '@/types/reduxStore/cartUpdate';
import { type AxiosError } from 'axios';

export const API_URL3: string = azURL('promoCodeApply');

export const API_URL4: string = azURL('promoCodeRemove');

export const emptyCartPageData = (): Action => ({
  type: actionConstants.CLEAR_CART_PAGE_DATA,
});

export type DiscountDispatchData = {
  id: string;
  fromPage: PageName;
  discountTypeId: number;
};

export const applyDiscountCodeCall =
  (
    data: DiscountDispatchData,
    checkoutPage: boolean,
    queryClient: QueryClient,
    getShipmentOptionsMutation?: GetShipmentOptionsMutation,
    URL: string = API_URL3,
    hideInStorePurchase = true,
  ): ThunkAction =>
  async (dispatch, getState, axios) => {
    // @ts-expect-error force cast from Dispatch<Action> to Dispatch<ThunkAction>
    const dispatchThunk = dispatch as Dispatch<ThunkAction>;

    dispatch({ type: actionConstants.PROMOCODE_APPLY_REQUESTING });

    try {
      const { id: promotionId, fromPage } = data;
      const response = await postATGData<PromoDataFromState>(axios, URL, {
        promotionId,
        fromPage,
      }); // if the request was made from the checkout page, request shipment
      // options but only if shippingAddressAvailable

      const shippingAddressAvailable = getState().orders.addressDetails.shippingAddressAvailable;
      const orderItems = getState().orders.details.orderItems;
      const onlyBOPUSItems = orderItems.filter(
        (item: { orderType: string }) => item.orderType === cartConstants.STOREORDER,
      );
      const orderIsOnlyBOPUS = orderItems.length === onlyBOPUSItems.length;
      if (checkoutPage && shippingAddressAvailable && !orderIsOnlyBOPUS) {
        try {
          await getShipmentOptionsMutation?.({});
        } catch (err) {
          dispatch({
            type: actionConstants.ORDER_DETAILS_FAILURE,
            err: err,
          });
        }
      }

      // this boolean controls whether url params are sent on the order details call. They should not be sent when on cart page.
      if (!checkoutPage) {
        hideInStorePurchase = false;
      }

      await dispatchThunk(fetchOrderDetails(hideInStorePurchase, queryClient));
      dispatch({
        type: actionConstants.PROMOCODE_APPLY_SUCCESS,
        data: response.data,
        value: data.id,
      });
    } catch (err: unknown) {
      const error = err as AxiosError;
      dispatch({
        type: actionConstants.PROMOCODE_APPLY_FAILURE,
        err: error.response,
        value: data.id,
        errorLog: err,
        data: data as unknown as PromoDataFromState,
      });
    }
  };

export const removeDiscountCodeCall =
  (data: DiscountDispatchData, URL: string = API_URL4, queryClient: QueryClient): ThunkAction =>
  async (dispatch, getState, axios) => {
    // @ts-expect-error force cast from Dispatch<Action> to Dispatch<ThunkAction>
    const dispatchThunk = dispatch as Dispatch<ThunkAction>;

    dispatch({ type: actionConstants.PROMOCODE_REMOVE_REQUESTING });

    try {
      const removeCouponDiscountData = {
        couponId: data.id,
        fromPage: data.fromPage,
      };
      const removeDealDiscountData = {
        promotionId: data.id,
        fromPage: data.fromPage,
      };
      const requestData =
        data.discountTypeId === 1 ? removeCouponDiscountData : removeDealDiscountData;
      const response = await postATGData<PromoDataFromState>(axios, URL, requestData);
      dispatch({
        type: actionConstants.PROMOCODE_REMOVE_SUCCESS,
        data: response.data,
        values: data.id,
      });
      dispatchThunk(fetchOrderDetails(undefined, queryClient));
    } catch (err: unknown) {
      const error = err as AxiosError;
      dispatch({
        type: actionConstants.PROMOCODE_REMOVE_FAILURE,
        err: error.message,
        values: data.id,
        errorLog: err,
        data: data as unknown as PromoDataFromState,
      });
    }
  };

export const resetDiscounts = (): Action => ({
  type: actionConstants.RESET_PROMO_CART,
});
